import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import {UtilityService} from "../shared/service/utility.service";

import {Observable} from "rxjs";
import {catchError, map, retry} from "rxjs/operators";
import {AppConfig} from "../shared/constants/app.config";
import * as uuid from 'uuid';

@Injectable()
export class ObservationVitalSignsService {
  constructor(
    readonly httpClient : HttpClient,
    readonly utilityService: UtilityService,

  ) { }

  /**
   * Generates the array of bundles to submit to cibmtr by splitting Observations to size 30(chunksize) per bundle
   * @param selectedResources
   * @param psScope
   */
  getBundles(selectedResources, psScope): Array<Observable<any>> {
    const selectedChunkResources = this.utilityService.chunk(
      selectedResources,
      this.utilityService.chunkSize
    );
    const bundles = [];

    selectedChunkResources.forEach((selectedChunkResource) => {
      bundles.push(this.getBundleEntry(selectedChunkResource, psScope));
    });
    return bundles;
  }

  /**
   * Post the Observation bundle to the cibmtr backend url
   * @param bundle - Bundle of vital sign observations
   */
  postBundle(bundle) {
    return this.httpClient.post(localStorage.getItem("cibmtr_url") + "Bundle", bundle).pipe(
      map(() => bundle),
      retry(1),
      catchError((error) => {
        throw error;
      })
    );
  }

  /**
   * Generate the max 30 sized bundle with new and/or updated Observation records for submission to cibmtr
   * @param selectedNewChunkResource
   * @param psScope
   */
  getBundleEntry(selectedNewChunkResource, psScope) {
    const entries = [];
    selectedNewChunkResource.forEach((selectedChunkElement) => {
      if (selectedChunkElement.state === "normal") {
        const { fullUrl, resource } = selectedChunkElement;
        entries.push({
          fullUrl: "urn:uuid:" + uuid.v4(),
          resource: {
            ...resource,
            meta: {
              security: [
                {
                  system: AppConfig.CIBMTR_CENTERS_NAMESPACE,
                  code: psScope,
                },
              ],
            },
            identifier: [
              {
                use: "official",
                system: AppConfig.FHIR_LOGICALID_NAMESPACE,
                value: fullUrl,
              },
            ],
          },
          request: {
            method: "PUT",
            url: `Observation/${resource.id}`,
          },
        });
      } else {
        const { fullUrl, resource } = selectedChunkElement;
        delete resource.id;
        entries.push({
          fullUrl: "urn:uuid:" + uuid.v4(),
          resource: {
            ...resource,
            status: "unknown",
            meta: {
              security: [
                {
                  system: AppConfig.CIBMTR_CENTERS_NAMESPACE,
                  code: psScope,
                },
              ],
            },
            identifier: [
              {
                use: "official",
                system: AppConfig.FHIR_LOGICALID_NAMESPACE,
                value: fullUrl,
              },
            ],
          },
          request: {
            method: "POST",
            url: "Observation",
          },
        });
      }
    });
    return {
      resourceType: "Bundle",
      type: "transaction",
      entry: entries,
    };
  }

  /**
   * Retrieve already submitted Observation vital-signs from the cibmtr fhir-server (via backend)
   * @param psScope
   * @param fhirUrl
   */
  getCibmtrObservationsVitalSigns(psScope, fhirUrl): Observable<any> {
    const greaterThan = "&date=ge" + localStorage.getItem("startDate");
    const lesserThan = "&date=le" + localStorage.getItem("endDate");
    const observationUrl = "Observation?subject=Patient/" + localStorage.getItem("patient_local_id");
    const security = "&_security=" + psScope;
    const countQuery = "&_count=500&category=vital-signs";
    const url = fhirUrl + observationUrl + security + countQuery + greaterThan + lesserThan;
    return this.httpClient.get(url);
  }

  /**
   * Submit Msg-Header for successful bundle submissions
   * @param msgHeaderBundle
   */
  submitMsgHeader(msgHeaderBundle: any) {
    const response = this.httpClient.post(localStorage.getItem("cibmtr_url") + "$process-message", msgHeaderBundle).pipe(
      map(() => msgHeaderBundle),
      retry(1),
      catchError((error) => {
        throw error;
      })
    );
    console.log(response);
    return response;
  }

  /**
   * Generate the Msg header to be sent on successful submission of vital-sign bundles
   */
  getMsgHeaderBundle(): any {
    const entries = [];
    entries.push({
      resource: {
        resourceType: "MessageHeader",
        event: {
          system: "http://cibmtr.org/codesystem/reporting-message",
          code: "batch-complete",
          display: "batch complete"
        },
        sender: {
          identifier: [
            {
              system: AppConfig.CIBMTR_CENTERS_NAMESPACE,
              value: this.utilityService.data.selectedOrg.value,
            },
          ],
        },
        "focus": [
          {
            identifier: [
              {
                use: "official",
                system: AppConfig.CIBMTR_CRID_NAMESPACE,
                value: this.utilityService.data.crid,
              },
            ],
            "display": "Patient with CRID " + this.utilityService.data.crid,
          }
        ],
      },
      request: {
        method: "POST",
        url: "MessageHeader",
      },
    });
    return {
      resourceType: "Bundle",
      type: "message",
      entry: entries,
    };
  }
}
